import React, { useMemo, useState } from 'react';
import { deepCloneArray } from '../../utils/array';
import { ModalGlobal } from '../modal/modal.Global';
import { FaFilter, FaPlus } from 'react-icons/fa';
import {
  createNewItem,
  dupplicateTabIntoItem,
  getSearch,
  update,
} from '../../services/item';
import { useDispatch, useSelector } from 'react-redux';
import { InputSimple } from '../form/input.Simple';
import { setOpenModales } from '../../store/global';
import { BtnSimple } from '../form/btn.Simple';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import { getId, isTabCampaignAccessible } from '../../utils/item';
import { Checkbox } from '../form/checkbox';
import { useSyncedCampaignFilter } from '../../hooks/useSyncedCampaignFilter';

const SPECIAL_TABS = [
  { id: 'text', label: 'Textes', display: 'text' },
  { id: 'img', label: 'Images', display: 'img' },
  { id: 'link', label: 'Liens', display: 'link' },
  { id: 'num', label: 'Nombres', display: 'num' },
  { id: 'conditions', label: 'Boutons', display: 'conditions' },
];

export const TabNew = props => {
  const { itemId } = props;

  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const listCampaignsId = useSelector(state => state.global.listCampaignsId);
  const canMj = isMjForItem(item, items) || canUseMjForItem(item, items);
  const dispatch = useDispatch();

  const [newItemLabel, setNewItemLabel] = useState('');
  const [searchedItem, setSearchedItem] = useState('');
  const [searchedItemsList, setSearchedItemsList] = useState([]);
  const [duplicateTab, setDuplicateTab] = useState(false);
  const [showCampaignFilter, setShowCampaignFilter] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] =
    useSyncedCampaignFilter(listCampaignsId, 'item-search-campaigns');
  const searchIn = {
    type: 'tab',
    universe: true,
    world: true,
    campaign: canMj === true ? false : true,
  };

  const currentTabs = useMemo(() => item?.tabs ?? [], [item?.tabs]);
  const specialTabIds = useMemo(
    () => new Set(currentTabs.map(tabObj => getId(tabObj))),
    [currentTabs]
  );
  const campaignOptions = useMemo(() => {
    if (!Array.isArray(listCampaignsId)) {
      return [];
    }
    return listCampaignsId
      .filter(Boolean)
      .map(id => ({ id, label: items?.[id]?.label || 'Dossier' }));
  }, [listCampaignsId, items]);

  const addTab = async itemInList => {
    const clonedLinks = deepCloneArray(item.tabs);
    const i = itemInList._id;
    clonedLinks.push({
      [i]: { _id: i, label: itemInList.label, display: 'grid' },
    });
    update(`items/${itemId}`, 'tabs', clonedLinks);

    setNewItemLabel('');
    setSearchedItemsList([]);
    setSearchedItem('');
    setDuplicateTab(false);
    dispatch(setOpenModales({ id: 'addTab', open: false }));
  };

  const search = async value => {
    setNewItemLabel('');
    setSearchedItem(value);
    setDuplicateTab(false);
    if (Array.isArray(selectedCampaignIds) && selectedCampaignIds.length === 0) {
      setSearchedItemsList([]);
      return;
    }
    const res = await getSearch(value, item, searchIn, selectedCampaignIds);
    if (Array.isArray(res)) {
      const filtered = res.filter(searchItem =>
        isTabCampaignAccessible(searchItem, items, listCampaignsId)
      );
      setSearchedItemsList(filtered);
    } else {
      setSearchedItemsList([]);
    }
  };

  const toggleSpecialTab = tabId => {
    const specialIds = SPECIAL_TABS.map(tab => tab.id);
    const clonedTabs = deepCloneArray(currentTabs);
    const nonSpecialTabs = clonedTabs.filter(
      tabObj => !specialIds.includes(getId(tabObj))
    );
    const presentSpecialIds = new Set(
      clonedTabs
        .map(tabObj => getId(tabObj))
        .filter(id => specialIds.includes(id))
    );
    if (presentSpecialIds.has(tabId)) {
      presentSpecialIds.delete(tabId);
    } else {
      presentSpecialIds.add(tabId);
    }

    const tabLookup = clonedTabs.reduce((acc, tabObj) => {
      acc[getId(tabObj)] = tabObj;
      return acc;
    }, {});

    const nextSpecialTabs = SPECIAL_TABS.filter(tab =>
      presentSpecialIds.has(tab.id)
    ).map(tab => {
      if (tabLookup[tab.id]) {
        return tabLookup[tab.id];
      }
      return {
        [tab.id]: {
          _id: tab.id,
          label: tab.label,
          display: tab.display,
        },
      };
    });

    update(`items/${itemId}`, 'tabs', [...nextSpecialTabs, ...nonSpecialTabs]);
  };

  const isSpecialTabActive = tabId => specialTabIds.has(tabId);

  return (
    <>
      <ModalGlobal
        idToOpen={'addTab'}
        title='Ajouter un onglet'
      >
        <div className='w-full flex items-center '>
          <div className='w-1/5'>créer un nouvel onglet</div>
          <InputSimple
            resetValue={newItemLabel}
            placeholder={'nom du nouvel item'}
            onChange={e => {
              setNewItemLabel(e.target.value);
              setSearchedItem('');
              setSearchedItemsList([]);
            }}
            onBlur={() => null}
          />
        </div>

        <div className='w-full mt-1'>
          <div className='text-xs uppercase text-contrast text-opacity-60 mb-05'>
            Onglets spéciaux
          </div>
          <div className='flex flex-wrap gap-05'>
            {SPECIAL_TABS.map(specialTab => {
              const active = isSpecialTabActive(specialTab.id);
              return (
                <BtnSimple
                  key={specialTab.id}
                  className={`text-xs ${
                    active ? 'bg-secondary' : 'bg-primary'
                  }`}
                  onClick={() => toggleSpecialTab(specialTab.id)}
                >
                  {active ? 'Retirer' : 'Ajouter'} {specialTab.label}
                </BtnSimple>
              );
            })}
          </div>
        </div>

        <div className='w-full flex flex-wrap items-center '>
          <div className='w-1/5'>chercher et ajouter un onglet</div>
          <div className='flex-1 flex items-center gap-05'>
            <InputSimple
              className='flex-1 m-0'
              resetValue={searchedItem}
              forcedValue={searchedItem?.label}
              placeholder={"nom de l'onglet"}
              onChange={e => search(e.target.value)}
              onBlur={() => null}
            />
            {campaignOptions.length > 0 && (
              <button
                type='button'
                className={`shrink-0 transition hover:opacity-70 ${
                  showCampaignFilter ||
                  selectedCampaignIds.length !== campaignOptions.length
                    ? 'text-secondary'
                    : 'text-contrast text-opacity-70'
                }`}
                title='Filtrer les dossiers'
                onClick={() => setShowCampaignFilter(prev => !prev)}
              >
                <FaFilter />
              </button>
            )}
          </div>
          {campaignOptions.length > 0 && showCampaignFilter && (
            <div className='w-full mt-03 max-h-8 overflow-auto bg-main bg-opacity-40 rounded p-04 border border-main-d border-opacity-40'>
              {campaignOptions.map(option => {
                const checked = selectedCampaignIds.includes(option.id);
                return (
                  <div
                    key={option.id}
                    className='py-02'
                  >
                    <Checkbox
                      checked={checked}
                      size='sm'
                      label={option.label}
                      labelClassName='text-xs'
                      onChange={() =>
                        setSelectedCampaignIds(prev =>
                          prev.includes(option.id)
                            ? prev.filter(id => id !== option.id)
                            : [...prev, option.id],
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
          <div className='w-full flex'>
            <div className='w-1/4'></div>
            <div>
              {searchedItemsList.map(searchItem => (
                <div
                  key={searchItem._id}
                  className='cursor-pointer flex gap-1 items-center'
                  onClick={() => {
                    setSearchedItem(searchItem);
                    setSearchedItemsList([]);
                    setDuplicateTab(false);
                  }}
                >
                  <div className='flex-1'>
                    {searchItem.label} - {searchItem.alias}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {searchedItem?.label && (
          <div className='w-full flex mt-1'>
            <div className='w-1/5'></div>
            <div className='flex gap-05 items-center'>
              <BtnSimple
                className={`text-xs ${duplicateTab ? 'bg-secondary' : 'bg-primary'}`}
                onClick={() => setDuplicateTab(v => !v)}
              >
                {duplicateTab ? 'Dupliquer: oui' : 'Dupliquer: non'}
              </BtnSimple>
              <div className='text-xs text-contrast text-opacity-60'>
                (cree une copie de l'onglet pour cet item)
              </div>
            </div>
          </div>
        )}

        <div className='w-full flex justify-center mt-1'>
          <BtnSimple
            className='flex gap-1 items-center'
            disabled={newItemLabel === '' && !searchedItem?.label}
            onClick={() =>
              newItemLabel !== ''
                ? createNewItem(
                    {
                      label: newItemLabel,
                      alias: 'de ' + item.label,
                      type: 'tab',
                      grid: [],
                      callback: addTab,
                    },
                    item
                  )
                : duplicateTab
                  ? dupplicateTabIntoItem(searchedItem._id, item, {
                      callback: addTab,
                    })
                  : addTab(searchedItem)
            }
          >
            <FaPlus />{' '}
            {newItemLabel !== ''
              ? `Ajouter l'onglet "${newItemLabel}"`
              : duplicateTab
                ? `Dupliquer l'onglet "${searchedItem?.label}"`
                : `Ajouter l'onglet "${searchedItem?.label}"`}
          </BtnSimple>
        </div>
      </ModalGlobal>
      <div
        className='cursor-pointer hover:opacity-50 transition pr-1'
        onClick={() => dispatch(setOpenModales({ id: 'addTab', open: true }))}
      >
        <FaPlus />
      </div>
    </>
  );
};
